import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LoadingButton } from '@lf/ui-primitives';
function childDisplayName(child) {
    return (child && (child.first_name || child.username)) || '';
}

const ImportTargetSelector = ({
    childProfiles,
    onImport,
    isImporting,
    error,
    onAddChild,
    autoTrigger = false,
}) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const autoTriggerFired = useRef(false);

    const children = useMemo(() => childProfiles || [], [childProfiles]);

    useEffect(() => {
        // Pre-check the first child as a UX nicety when the dialog opens.
        if (children.length > 0) {
            setSelectedIds([children[0].id]);
        } else {
            setSelectedIds([]);
        }
    }, [children]);

    useEffect(() => {
        // Resume the import after a sign-in round-trip: single-child
        // parents go straight through; multi-child parents land on the
        // open child-picker dialog so one click completes the import.
        if (!autoTrigger || autoTriggerFired.current) return;
        if (children.length === 1) {
            autoTriggerFired.current = true;
            onImport([children[0].id]);
        } else if (children.length > 1) {
            autoTriggerFired.current = true;
            setIsDialogOpen(true);
        }
    }, [autoTrigger, children, onImport]);

    if (children.length === 0) {
        return (
            <div className="flex justify-end">
                <button
                    type="button"
                    className="bg-blue-500 text-white py-2 px-4 rounded"
                    onClick={onAddChild}>
                    Add a child to import
                </button>
            </div>
        );
    }

    if (children.length === 1) {
        const child = children[0];
        return (
            <div className="flex flex-col items-end gap-2">
                <LoadingButton
                    text={`Import study plan to ${childDisplayName(child)}`}
                    isLoading={isImporting}
                    onClick={() => onImport([child.id])} />
                {error && (
                    <div className="text-red-500 text-sm">
                        {error.message || 'Could not import. Please try again.'}
                    </div>
                )}
            </div>
        );
    }

    const toggleId = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
    };

    const handleConfirm = async () => {
        if (selectedIds.length === 0) return;
        await onImport(selectedIds);
        setIsDialogOpen(false);
    };

    return (
        <div className="flex flex-col items-end gap-2">
            <button
                type="button"
                className="bg-blue-500 text-white py-2 px-4 rounded"
                onClick={() => setIsDialogOpen(true)}>
                Import study plan
            </button>
            {error && (
                <div className="text-red-500 text-sm">
                    {error.message || 'Could not import. Please try again.'}
                </div>
            )}

            {isDialogOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-[28rem] text-left">
                        <h2 className="text-lg font-medium mb-4">Import to which child?</h2>
                        <div className="space-y-2 mb-4">
                            {children.map((child) => (
                                <label key={child.id} className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(child.id)}
                                        onChange={() => toggleId(child.id)} />
                                    <span>{childDisplayName(child)}</span>
                                </label>
                            ))}
                        </div>
                        <div className="flex justify-end gap-2">
                            <LoadingButton
                                text={`Import to selected (${selectedIds.length})`}
                                isLoading={isImporting}
                                disabled={selectedIds.length === 0}
                                onClick={handleConfirm} />
                            <button
                                type="button"
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded"
                                onClick={closeDialog}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImportTargetSelector;
